import { useState, useEffect, useCallback, useRef } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import axios from 'axios';
import { Bell, X, Check, CheckCheck, AlertTriangle, ShieldAlert, Clock, Gauge, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_STYLES = {
  critical: { bg: 'bg-red-500/10', border: 'border-red-500/30', icon: ShieldAlert, color: 'text-red-400', dot: 'bg-red-500' },
  warning: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', icon: AlertTriangle, color: 'text-amber-400', dot: 'bg-amber-500' },
  info: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: Gauge, color: 'text-blue-400', dot: 'bg-blue-500' },
};

export default function NotificationBell() {
  const { token } = useAdminAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [counts, setCounts] = useState({ unread: 0, critical_unresolved: 0 });
  const panelRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCounts = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/api/mechanic/admin/notifications/unread-count`, { headers });
      setCounts(res.data);
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/api/mechanic/admin/notifications`, { headers });
      setNotifications(res.data || []);
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Poll counts every 20 seconds
  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 20000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  // Fetch full list when panel opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleMarkRead = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/mechanic/admin/notifications/${id}/read`, {}, { headers });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setCounts(prev => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
    } catch { /* silent */ }
  };

  const handleResolve = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/mechanic/admin/notifications/${id}/resolve`, {}, { headers });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_resolved: true } : n));
      setCounts(prev => ({ ...prev, critical_unresolved: Math.max(0, prev.critical_unresolved - 1) }));
    } catch { /* silent */ }
  };

  const handleMaintenanceDone = async (n) => {
    try {
      await axios.post(`${API_URL}/api/mechanic/admin/notifications/${n.id}/maintenance-done`, {}, { headers });
      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true, is_resolved: true, done: true } : x));
      setCounts(prev => ({ ...prev, unread: Math.max(0, prev.unread - (n.is_read ? 0 : 1)) }));
      toast.success('Entretien inscrit comme fait au kilométrage actuel');
    } catch { toast.error("Impossible d'inscrire l'entretien"); }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API_URL}/api/mechanic/admin/notifications/mark-all-read`, {}, { headers });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setCounts(prev => ({ ...prev, unread: 0 }));
    } catch { /* silent */ }
  };

  const badgeCount = counts.unread + counts.critical_unresolved;
  const hasCritical = counts.critical_unresolved > 0;

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
        className={`relative p-2 rounded-lg transition-colors ${
          hasCritical ? 'text-red-400 hover:bg-red-500/10 animate-pulse' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
        }`}
      >
        <Bell size={20} />
        {badgeCount > 0 && (
          <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold rounded-full ${
            hasCritical ? 'bg-red-500 text-white' : 'bg-amber-500 text-black'
          }`}>
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div className="absolute right-0 top-12 w-96 max-h-[500px] bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden z-50" data-testid="notification-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50 bg-slate-900">
            <h3 className="text-white font-semibold text-sm">Notifications</h3>
            <div className="flex items-center gap-2">
              {counts.unread > 0 && (
                <button onClick={handleMarkAllRead} className="text-xs text-slate-400 hover:text-amber-400 flex items-center gap-1" data-testid="mark-all-read">
                  <CheckCheck size={14} /> Tout lu
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white"><X size={16} /></button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto max-h-[420px] divide-y divide-slate-800/50">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                <Bell size={32} className="mx-auto mb-2 opacity-30" />
                <p>Aucune notification</p>
              </div>
            ) : (
              notifications.map(n => {
                const sev = SEVERITY_STYLES[n.severity] || SEVERITY_STYLES.info;
                const Icon = sev.icon;
                const isUnresolved = !n.is_resolved && (n.severity === 'critical' || n.severity === 'warning');

                return (
                  <div
                    key={n.id}
                    className={`px-4 py-3 ${!n.is_read ? 'bg-slate-800/30' : ''} ${isUnresolved ? `border-l-2 ${n.severity === 'critical' ? 'border-l-red-500' : 'border-l-amber-500'}` : ''}`}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className="flex gap-3">
                      <div className={`w-8 h-8 rounded-lg ${sev.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <Icon size={14} className={sev.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium ${isUnresolved ? sev.color : n.is_read ? 'text-slate-400' : 'text-white'}`}>
                            {n.title}
                          </p>
                          {!n.is_read && <span className={`w-2 h-2 rounded-full ${sev.dot} flex-shrink-0 mt-1.5`} />}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-[10px] text-slate-600">{n.created_at?.slice(0, 16).replace('T', ' ')}</span>
                          {!n.is_read && (
                            <button onClick={() => handleMarkRead(n.id)} className="text-[10px] text-slate-500 hover:text-amber-400 flex items-center gap-0.5">
                              <Check size={10} /> Lu
                            </button>
                          )}
                          {n.type === 'maintenance_due' && !n.is_resolved && (
                            <button onClick={() => handleMaintenanceDone(n)} className="text-xs font-semibold text-emerald-400 hover:text-emerald-300 flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/10" data-testid={`maintenance-done-${n.id}`}>
                              <Wrench size={10} /> Entretien fait
                            </button>
                          )}
                          {n.type === 'maintenance_due' && n.is_resolved && (
                            <span className="text-[10px] text-emerald-500 flex items-center gap-0.5"><CheckCheck size={10} /> Fait</span>
                          )}
                          {isUnresolved && n.type !== 'maintenance_due' && (
                            <button onClick={() => handleResolve(n.id)} className="text-[10px] text-slate-500 hover:text-emerald-400 flex items-center gap-0.5" data-testid={`resolve-${n.id}`}>
                              <CheckCheck size={10} /> Corrige
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
